// @flow
/* eslint-disable ta-code-conventions/has-matching-storydoc */
/* eslint-disable ta-code-conventions/has-matching-test */
import * as React from 'react';
import { getObjectId } from '@ta/common.global';
import classnames from '@ta/common.classnames';
import { featureIsEnabled } from '@ta/common.features';
import Logging from '@ta/common.logging';
import { ResponsiveImage } from '@ta/media.image';
import { Link } from '@ta/common.routing';
import ObfuscatedLink from '@ta/common.obfuscated-link';
import { BubbleRatingWithReviewCount } from '@ta/styleguide.bubble-rating';
import { InteractionLogger } from '@ta/tracking.interactions';
import { QueriedTripFlowSelector } from '@ta/trips.trip-flow-selector';
import { BookmarkIcon } from '@ta/trips.bookmark-icon';
import { NoPhotoAvailableByPlaceType, PlaceTypeIcon } from '@ta/social.place-type';
import type { Photo } from '@ta/common.photo-carousel';
import styles from './POIObject.less';

const buildLocationUrlForCard = (location: any) => {
  if (location && location.url) {
    Logging.warn('POIObject using location.url instead of location.route.url!');
  }
  return location?.route?.url || location?.url;
};

type POIObjectViewModel = {
  objectId: string,
  object: any,
  name: string,
  secondaryText: ?string,
  photo: ?Photo,
  reviewCount: number,
  rating: number,
  url: string,
  obfuscatedUrl: boolean,
};

type Size = 'small' | 'large';
type Props = {|
  poi: POIObjectViewModel,
  size: Size,
  borderRadius: string,
  /** TODO: Once video modal is an actual overlay, we can render images responsively  */
  notLazy: boolean,
  showSavesIcon: boolean,
|};

const POIObject = ({ poi, notLazy, showSavesIcon, size, borderRadius }: Props) => {
  const length = size === 'small' ? '60px' : '66px';
  const hasUrl = poi.url && poi.url.length > 1;
  const shouldRenderBubbleCount = poi.reviewCount > 0 && poi.rating > 0;
  const viewBody = (
    <div className={`${styles.poi_block} ${styles.relative_container}`}>
      <div className={styles.poi_thumbnail}>
        {poi.photo ? (
          <ResponsiveImage
            sources={poi.photo.photoSizes}
            width={length}
            height={length}
            notLazy={notLazy}
            borderRadius={borderRadius}
          />
        ) : (
          <div style={{ width: length, height: length }}>
            <NoPhotoAvailableByPlaceType location={poi.object} />
          </div>
        )}
      </div>
      <div className={styles.poi_info_vertical_center}>
        <div className={styles.poi_info}>
          <div className={`${styles.poi_name} ${hasUrl ? 'ui_link' : ''}`} title={poi.name}>
            {poi.name}
          </div>
          {shouldRenderBubbleCount && (
            <BubbleRatingWithReviewCount numReviews={poi.reviewCount} rating={poi.rating} />
          )}
          <div
            className={classnames(styles.poi_location, {
              [styles.with_bubble]: shouldRenderBubbleCount,
            })}
          >
            {poi.object.placeType && poi.object.placeType !== 'CITY' && (
              <PlaceTypeIcon placeType={poi.object.placeType} className={styles.meta_icon} />
            )}
            {poi.secondaryText}
          </div>
        </div>
      </div>
      {showSavesIcon && (
        <div
          className={classnames(styles.save_to_trip_wrapper, {
            [styles.heart]: featureIsEnabled('trips_saves_heart'),
          })}
        >
          <QueriedTripFlowSelector object={poi.object}>
            {(openFlow, active) => (
              <InteractionLogger>
                {logInteraction => (
                  <BookmarkIcon
                    bookmarkStyle="flat"
                    bookmarkSize="20"
                    active={active}
                    onClick={e => {
                      openFlow();
                      logInteraction(e, 'save', { locationId: poi.object.locationId });
                    }}
                  />
                )}
              </InteractionLogger>
            )}
          </QueriedTripFlowSelector>
        </div>
      )}
    </div>
  );

  // If there's no URL, don't link
  if (!hasUrl) {
    return viewBody;
  }

  return (
    <InteractionLogger>
      {logInteraction =>
        poi.obfuscatedUrl ? (
          <ObfuscatedLink
            encodedUrl={poi.url}
            onClick={e =>
              logInteraction(e, 'linkedPoiClick', { locationId: poi.object.locationId })
            }
          >
            {viewBody}
          </ObfuscatedLink>
        ) : (
          <Link
            href={poi.url}
            onClick={e =>
              logInteraction(e, 'linkedPoiClick', { locationId: poi.object.locationId })
            }
          >
            {viewBody}
          </Link>
        )
      }
    </InteractionLogger>
  );
};

POIObject.defaultProps = {
  notLazy: false,
  showSavesIcon: false,
  size: 'small',
  borderRadius: '2px',
};

/** Creates an inline object of the view model */
export const viewModelForObject = (poi: any): POIObjectViewModel => {
  let secondaryText;
  if (poi.placeType === 'CITY') {
    secondaryText = poi.additionalNames.longOnlyParent;
  } else {
    secondaryText = poi.parent?.additionalNames.long;
  }

  const objectId = getObjectId(poi);
  const obfuscatedUrl = poi.__typename === 'AttractionProductInformation';

  return {
    objectId,
    object: poi,
    name: poi.name,
    secondaryText,
    photo: poi.thumbnail,
    reviewCount: poi.reviewSummary ? poi.reviewSummary.count : 0,
    rating: poi.reviewSummary ? poi.reviewSummary.rating : 0,
    url: buildLocationUrlForCard(poi),
    obfuscatedUrl,
  };
};

/** A hook to get a memoized view model */
export const useViewModelForObject = (poi: any): POIObjectViewModel =>
  React.useMemo(() => viewModelForObject(poi), [poi]);

/** A hook to get a memoized view model for many POIs */
export const useViewModelForObjects = (pois: any[]): POIObjectViewModel[] =>
  React.useMemo(() => pois.map(viewModelForObject), [pois]);

/** The POIObject props */
export default (React.memo<Props>(POIObject): any);
